import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Profile, ProfilePermissions } from '../../../types/models';

export interface ProfileFormValues {
  name: string;
  description: string;
  permissions: ProfilePermissions;
}

interface ProfileFormDialogProps {
  open: boolean;
  initial?: Profile | null;
  onClose: () => void;
  onSubmit: (values: ProfileFormValues) => Promise<void> | void;
  saving?: boolean;
}

const turnOffSection = (next: ProfilePermissions, section: keyof ProfilePermissions): void => {
  const sectionObj = next[section] as any;
  Object.keys(sectionObj).forEach((g) => {
    if (sectionObj[g] && typeof sectionObj[g] === 'object') {
      Object.keys(sectionObj[g]).forEach((f) => {
        if (typeof sectionObj[g][f] === 'boolean') sectionObj[g][f] = false;
      });
    }
  });
};

const defaultPermissions = (): ProfilePermissions => ({
  userMgmt: {
    view: { canView: false, canCsvDownload: false },
    edit: { canCreateNew: false, canCsvImport: false, canBulkUpdate: false, canUpdate: false, canDelete: false, canChangePassword: false },
  },
  scenarioMgmt: {
    view: { canView: false, canCsvDownload: false },
    edit: { canCreateNew: false, canCsvImport: false, canUpdate: false, canDelete: false, canCustomScenarioUpdate: false },
  },
  analytics: {
    view: { canView: false, canCsvDownload: false },
  },
  profileMgmt: {
    view: { canView: false },
    edit: { canCreateNew: false, canUpdate: false, canDelete: false },
  },
});

const ProfileFormDialog: React.FC<ProfileFormDialogProps> = ({ open, initial, onClose, onSubmit, saving }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<ProfilePermissions>(defaultPermissions());
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setDescription(initial?.description || '');
      setPermissions(
        initial
          ? JSON.parse(JSON.stringify(initial.permissions))
          : defaultPermissions()
      );
      setError('');
    }
  }, [open, initial]);

  const hasAnyPermission = Object.values(permissions).some((section) =>
    Object.values(section).some((group) =>
      group !== null && typeof group === 'object' && Object.values(group as Record<string, unknown>).some((v) => v === true)
    )
  );

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('名称は必須です');
      return;
    }
    if (trimmed.length > 100) {
      setError('名称は100文字以内で入力してください');
      return;
    }
    try {
      await onSubmit({ name: trimmed, description: description.trim(), permissions });
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
    }
  };

  const setFlag = (
    section: keyof ProfilePermissions,
    group: string,
    flag: string,
    value: boolean
  ) => {
    setPermissions((p) => {
      const next = JSON.parse(JSON.stringify(p)) as ProfilePermissions;
      (next[section] as any)[group][flag] = value;
      if (flag === 'canView' && !value) {
        turnOffSection(next, section);
      } else if (value) {
        (next[section] as any).view.canView = true;
      }
      if (section === 'userMgmt' && group === 'edit' && flag === 'canCsvImport' && value) {
        const editObj = (next.userMgmt as any).edit;
        Object.keys(editObj).forEach((f) => { if (typeof editObj[f] === 'boolean') editObj[f] = true; });
      }
      return next;
    });
  };

  const toggleGroup = (section: keyof ProfilePermissions, group: string) => {
    setPermissions((p) => {
      const next = JSON.parse(JSON.stringify(p)) as ProfilePermissions;
      const groupObj = (next[section] as any)[group];
      const flags = Object.keys(groupObj).filter((k) => typeof groupObj[k] === 'boolean');
      const allOn = flags.every((flag) => groupObj[flag] === true);
      flags.forEach((flag) => { groupObj[flag] = !allOn; });
      if (group === 'view' && allOn) {
        turnOffSection(next, section);
      } else if (!allOn) {
        (next[section] as any).view.canView = true;
      }
      return next;
    });
  };

  const toggleSection = (section: keyof ProfilePermissions) => {
    setPermissions((p) => {
      const next = JSON.parse(JSON.stringify(p)) as ProfilePermissions;
      const sectionObj = next[section] as any;
      const groups = Object.keys(sectionObj).filter((k) => typeof sectionObj[k] === 'object' && sectionObj[k] !== null);
      const allOn = groups.every((group) =>
        Object.keys(sectionObj[group]).filter((k) => typeof sectionObj[group][k] === 'boolean').every((flag) => sectionObj[group][flag] === true)
      );
      groups.forEach((group) => {
        Object.keys(sectionObj[group]).filter((k) => typeof sectionObj[group][k] === 'boolean').forEach((flag) => {
          sectionObj[group][flag] = !allOn;
        });
      });
      return next;
    });
  };

  const renderSectionHeader = (label: string, section: keyof ProfilePermissions) => {
    const sectionObj = permissions[section] as any;
    const allFlags: boolean[] = Object.keys(sectionObj).flatMap((g) =>
      sectionObj[g] && typeof sectionObj[g] === 'object'
        ? Object.keys(sectionObj[g]).filter((k) => typeof sectionObj[g][k] === 'boolean').map((k) => sectionObj[g][k] as boolean)
        : []
    );
    const allOn = allFlags.length > 0 && allFlags.every(Boolean);
    const anyOn = allFlags.some(Boolean);
    return (
      <FormControlLabel
        sx={{ display: 'flex', mb: 0.5, ml: 0 }}
        control={<Checkbox size="small" checked={allOn} indeterminate={anyOn && !allOn} onChange={() => toggleSection(section)} sx={{ p: 0.25 }} />}
        label={<Typography variant="subtitle2" color="text.primary" fontWeight={600}>{label}</Typography>}
      />
    );
  };

  const renderGroupHeader = (label: string, section: keyof ProfilePermissions, group: string, addTopMargin = false) => {
    const groupObj = (permissions[section] as any)[group];
    const flags = Object.keys(groupObj).filter((k) => typeof groupObj[k] === 'boolean');
    const allOn = flags.length > 0 && flags.every((f) => groupObj[f] === true);
    const anyOn = flags.some((f) => groupObj[f] === true);
    return (
      <FormControlLabel
        sx={{ display: 'flex', pl: 1, ml: 0, ...(addTopMargin ? { mt: 0.5 } : {}) }}
        control={<Checkbox size="small" checked={allOn} indeterminate={anyOn && !allOn} onChange={() => toggleGroup(section, group)} sx={{ p: 0.25 }} />}
        label={<Typography variant="body2" color="text.secondary">{label}</Typography>}
      />
    );
  };

  const renderSwitch = (
    label: string,
    section: keyof ProfilePermissions,
    group: string,
    flag: string,
    indent: number = 1
  ) => {
    const checked = !!((permissions[section] as any)[group]?.[flag]);
    return (
      <FormControlLabel
        key={`${section}.${group}.${flag}`}
        sx={{ display: 'flex', pl: indent * 3 + 1, ml: 0 }}
        control={<Checkbox size="small" checked={checked} onChange={(e) => setFlag(section, group, flag, e.target.checked)} sx={{ p: 0.25 }} />}
        label={<Typography variant="caption">{label}</Typography>}
      />
    );
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'プロファイル編集' : '新規プロファイル'}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 100 }}
          />
          <TextField
            label="説明"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            inputProps={{ maxLength: 500 }}
          />

          <Divider />
          <Typography variant="subtitle1" fontWeight="bold">権限設定</Typography>

          {/* ユーザー情報 */}
          <Box>
            {renderSectionHeader('ユーザー情報', 'userMgmt')}
            {renderGroupHeader('閲覧', 'userMgmt', 'view')}
            {renderSwitch('一覧', 'userMgmt', 'view', 'canView')}
            {renderSwitch('CSVダウンロード', 'userMgmt', 'view', 'canCsvDownload')}
            {renderGroupHeader('編集', 'userMgmt', 'edit', true)}
            {renderSwitch('新規登録', 'userMgmt', 'edit', 'canCreateNew')}
            {renderSwitch('CSVインポート', 'userMgmt', 'edit', 'canCsvImport')}
            {renderSwitch('一括変更', 'userMgmt', 'edit', 'canBulkUpdate')}
            {renderSwitch('個別編集', 'userMgmt', 'edit', 'canUpdate')}
            {renderSwitch('個別削除', 'userMgmt', 'edit', 'canDelete')}
            {renderSwitch('パスワード変更', 'userMgmt', 'edit', 'canChangePassword')}
          </Box>

          <Divider />

          {/* ロープレ設定 */}
          <Box>
            {renderSectionHeader('ロープレ設定', 'scenarioMgmt')}
            {renderGroupHeader('閲覧', 'scenarioMgmt', 'view')}
            {renderSwitch('一覧', 'scenarioMgmt', 'view', 'canView')}
            {renderSwitch('CSVダウンロード', 'scenarioMgmt', 'view', 'canCsvDownload')}
            {renderGroupHeader('編集', 'scenarioMgmt', 'edit', true)}
            {renderSwitch('新規登録', 'scenarioMgmt', 'edit', 'canCreateNew')}
            {renderSwitch('CSVインポート', 'scenarioMgmt', 'edit', 'canCsvImport')}
            {renderSwitch('個別編集', 'scenarioMgmt', 'edit', 'canUpdate')}
            {renderSwitch('個別削除', 'scenarioMgmt', 'edit', 'canDelete')}
            {renderSwitch('カスタム評価編集', 'scenarioMgmt', 'edit', 'canCustomScenarioUpdate')}
          </Box>

          <Divider />

          {/* 利用分析 */}
          <Box>
            {renderSectionHeader('利用分析', 'analytics')}
            {renderGroupHeader('閲覧', 'analytics', 'view')}
            {renderSwitch('一覧', 'analytics', 'view', 'canView')}
            {renderSwitch('CSVダウンロード', 'analytics', 'view', 'canCsvDownload')}
          </Box>

          <Divider />

          {/* 権限プロファイル */}
          <Box>
            {renderSectionHeader('権限プロファイル', 'profileMgmt')}
            {renderGroupHeader('閲覧', 'profileMgmt', 'view')}
            {renderSwitch('一覧', 'profileMgmt', 'view', 'canView')}
            {renderGroupHeader('編集', 'profileMgmt', 'edit', true)}
            {renderSwitch('新規登録', 'profileMgmt', 'edit', 'canCreateNew')}
            {renderSwitch('個別編集', 'profileMgmt', 'edit', 'canUpdate')}
            {renderSwitch('個別削除', 'profileMgmt', 'edit', 'canDelete')}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving || !hasAnyPermission}>
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileFormDialog;
